import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { paisa, type Money } from '@fleet/shared';
import { useRecordPaymentMutation, useSettingsQuery } from '@/api/endpoints';
import { Button } from '@/design/Button';
import { MoneyText } from '@/design/Data';
import { DateTimeInput, Field, MoneyInput, SelectInput, TextInput } from '@/design/Field';
import { Modal } from '@/design/Modal';
import { useToast } from '@/design/Toast';
import { readApiError } from '@/lib/apiError';
import { fromInputValue, useTime } from '@/lib/time';

/**
 * Taking money.
 *
 * A modal, and one of the clearest cases for one: four fields, opened from a
 * row, and the list behind it is what you want to be looking at again the
 * moment it closes.
 *
 * Two decisions worth knowing about:
 *
 * - **The amount starts at the full balance.** The common case is somebody
 *   paying what they owe, so that is what the box says; a part payment is a
 *   correction rather than a fresh calculation.
 * - **The time is a field, not `now()`.** Cash gets taken at handover and typed
 *   in later, and a revenue report that puts Tuesday's money in Thursday is a
 *   report the owner cannot reconcile against the till.
 */

const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'fonepay', label: 'Fonepay QR' },
  { value: 'card', label: 'Card' },
  { value: 'cheque', label: 'Cheque' },
] as const;

const KINDS = [
  { value: 'payment', label: 'Payment' },
  { value: 'deposit', label: 'Deposit / advance' },
] as const;

/** 13 ÷ 113, because rates here include VAT rather than adding it on. */
const VAT_FRACTION_OF_GROSS = 13 / 113;

/** What is left of a VAT-inclusive amount once the tax is taken back out. */
function withoutVat(gross: number): number {
  return Math.round(gross - gross * VAT_FRACTION_OF_GROSS);
}

export interface PayableBooking {
  readonly id: string;
  readonly reference: string;
  readonly customerName: string;
  /** In paisa. What the amount box starts at. */
  readonly owed: number;
  /** An advance on a trip that has not happened yet. Defaults the kind. */
  readonly isAdvance?: boolean;
}

interface Props {
  readonly booking: PayableBooking | null;
  readonly onClose: () => void;
}

export function PaymentModal({ booking, onClose }: Props): ReactElement {
  const { toInputValue } = useTime();
  const [record, saving] = useRecordPaymentMutation();
  const toast = useToast();

  // Whether the with/without-VAT question exists at all. Cached by RTK Query,
  // so this is one request for the whole session rather than one per open.
  const settings = useSettingsQuery();
  const vatRegistered = settings.data?.vat_registered === true;

  const [amount, setAmount] = useState<Money>(paisa(0));
  const [kind, setKind] = useState<string>('payment');
  const [method, setMethod] = useState<string>('cash');
  const [withVat, setWithVat] = useState(true);
  const [reference, setReference] = useState('');
  const [receivedAt, setReceivedAt] = useState('');

  // Reset for whichever booking was opened, so last time's amount is never
  // sitting in the box waiting to be attached to the wrong customer.
  useEffect(() => {
    if (booking === null) return;
    setAmount(paisa(booking.owed));
    setKind(booking.isAdvance === true ? 'deposit' : 'payment');
    setMethod('cash');
    setWithVat(true);
    setReference('');
    setReceivedAt(toInputValue(new Date().toISOString() as never));
  }, [booking?.id, booking?.owed, booking?.isAdvance, toInputValue]);

  const problems = readApiError(saving.error);

  async function save(): Promise<void> {
    if (booking === null) return;
    try {
      await record({
        booking: booking.id,
        amount,
        kind: kind as never,
        method: method as never,
        with_vat: withVat,
        reference,
        received_at: fromInputValue(receivedAt),
      }).unwrap();

      toast.done(`Rs ${(Number(amount) / 100).toLocaleString('en-IN')} recorded for ${booking.customerName}.`);
      onClose();
    } catch {
      // The message belongs in the modal: it is about what was typed in it.
    }
  }

  const needsReference = method === 'cheque' || method === 'fonepay';

  return (
    <Modal
      open={booking !== null}
      onClose={onClose}
      title={
        booking === null
          ? 'Take payment'
          : `${booking.isAdvance === true ? 'Take an advance' : 'Take payment'} — ${booking.reference}`
      }
      note={
        booking === null
          ? undefined
          : booking.isAdvance === true
            ? `${booking.customerName}, before the trip. Full price Rs ${(booking.owed / 100).toLocaleString('en-IN')}.`
            : `${booking.customerName} owes Rs ${(booking.owed / 100).toLocaleString('en-IN')}`
      }
      footer={
        <>
          <Button
            intent="primary"
            size="touch"
            type="submit"
            form="payment-form"
            disabled={saving.isLoading || Number(amount) <= 0}
          >
            {saving.isLoading ? 'Recording' : 'Record payment'}
          </Button>
          <Button size="touch" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <form
        id="payment-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        className="flex flex-col gap-4"
      >
        {problems.detail !== undefined && (
          <p
            className="border border-alert/40 border-l-[3px] border-l-alert bg-alert-wash px-3 py-2 font-medium text-alert"
            role="alert"
          >
            {problems.detail}
          </p>
        )}

        <Field
          label="Amount"
          required
          error={problems.fields['amount']}
        >
          {({ id, describedBy }) => (
            <MoneyInput id={id} describedBy={describedBy} value={amount} onChange={setAmount} />
          )}
        </Field>

        <Field label="What kind" error={problems.fields['kind']}>
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              describedBy={describedBy}
              value={kind}
              onChange={setKind}
              options={[...KINDS]}
            />
          )}
        </Field>

        <Field label="How it arrived" required error={problems.fields['method']}>
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              describedBy={describedBy}
              value={method}
              onChange={setMethod}
              options={[...METHODS]}
            />
          )}
        </Field>

        {/*
          "With bill or without?" is a real conversation at the counter, and
          the two are different money for the same trip. Rates here include
          VAT, so choosing "without" takes the 13% back out rather than adding
          anything — and the flag has to travel with the payment, or the VAT
          return counts tax on money that never carried any.

          Hidden entirely for a business that is not VAT registered: the
          question does not arise, and a toggle that changes nothing is a
          toggle somebody will eventually get wrong.
        */}
        {vatRegistered && (
          <Field
            label="Bill — without one, the 13% comes out of the amount"
            error={problems.fields['with_vat']}
          >
            {({ id, describedBy }) => (
              <SelectInput
                id={id}
                describedBy={describedBy}
                value={withVat ? 'with' : 'without'}
                onChange={(value) => {
                  const next = value === 'with';
                  setWithVat(next);
                  // Move the amount to match, so the number on screen is the
                  // number to ask for. Only when it is still the suggested
                  // figure — a part payment somebody has typed is theirs.
                  if (booking === null) return;
                  if (next && Number(amount) === withoutVat(booking.owed)) {
                    setAmount(paisa(booking.owed));
                  } else if (!next && Number(amount) === booking.owed) {
                    setAmount(paisa(withoutVat(booking.owed)));
                  }
                }}
                options={[
                  { value: 'with', label: 'With VAT — invoice' },
                  { value: 'without', label: 'Without VAT — no bill' },
                ]}
              />
            )}
          </Field>
        )}

        {vatRegistered && !withVat && booking !== null && (
          <p className="border-l-[3px] border-l-attention bg-attention-wash px-3 py-2 text-small">
            No bill for this one. With a bill it would be{' '}
            <MoneyText amount={paisa(booking.owed)} />; the{' '}
            <MoneyText amount={paisa(booking.owed - withoutVat(booking.owed))} /> of VAT comes
            off the booking as well, so nothing is left owing and no invoice can be raised
            against it.
          </p>
        )}

        {needsReference && (
          <Field
            label={method === 'cheque' ? 'Cheque number' : 'Fonepay transaction'}
            error={problems.fields['reference']}
          >
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                describedBy={describedBy}
                identifier
                value={reference}
                onChange={setReference}
              />
            )}
          </Field>
        )}

        <Field
          label="When it was received"
          required
          error={problems.fields['received_at']}
        >
          {({ id, describedBy }) => (
            <DateTimeInput
              id={id}
              describedBy={describedBy}
              value={receivedAt}
              onChange={setReceivedAt}
            />
          )}
        </Field>

        {/*
          The no-bill case is excluded, and that is not cosmetic: the amount is
          genuinely lower there, but the VAT comes off the booking too, so
          nothing is left owing. Saying "Rs 1,380 will still be owed" under the
          notice that says the opposite is the screen contradicting itself.
        */}
        {booking !== null &&
          Number(amount) > 0 &&
          Number(amount) < (withVat ? booking.owed : withoutVat(booking.owed)) && (
            <p className="border-l-[3px] border-l-attention bg-attention-wash px-3 py-2 text-small">
              Part payment.{' '}
              <MoneyText
                amount={paisa(
                  (withVat ? booking.owed : withoutVat(booking.owed)) - Number(amount),
                )}
              />{' '}
              will still be owed.
            </p>
          )}
      </form>
    </Modal>
  );
}
